import { forwardRef, useEffect, useImperativeHandle, useRef, type HTMLAttributes } from 'react';
import { Flex, cn } from '@bridebook/aura';

interface Props extends HTMLAttributes<HTMLDivElement> {
  /**
   * When active index changes, the list automatically scrolls to show it
   */
  activeIndex?: number;
  /**
   * Use a smooth scrolling animation
   */
  smooth?: boolean;
  /**
   * Position the active item in the center, even if it's already fully visible
   */
  alwaysScrollCenter?: boolean;
}

/**
 * A horizontally scrollable list that automatically scrolls to the active item based on the activeIndex prop.
 */
export const HorizontalAutoScroll = forwardRef<HTMLDivElement | null, Props>(
  (
    { children, activeIndex = -1, smooth = true, alwaysScrollCenter = false, className, ...props },
    forwardedRef,
  ) => {
    const containerRef = useRef<HTMLDivElement>(null);

    useImperativeHandle<HTMLDivElement | null, HTMLDivElement | null>(
      forwardedRef,
      () => containerRef.current,
    );

    /**
     * Scroll to active element when activeIndex changes
     */
    useEffect(() => {
      if (containerRef.current && activeIndex >= 0) {
        const container = containerRef.current;
        const activeElement = container.children[activeIndex] as HTMLElement;
        if (!activeElement) return;

        // Check if the element is fully visible within the container
        const containerRect = container.getBoundingClientRect();
        const elementRect = activeElement.getBoundingClientRect();
        const isFullyVisible =
          elementRect.left >= containerRect.left && elementRect.right <= containerRect.right;

        if (isFullyVisible && !alwaysScrollCenter) return;

        const containerWidth = container.clientWidth;
        const elementLeftOffset = activeElement.offsetLeft;
        const elementWidth = activeElement.clientWidth;

        // Calculate new scroll position to center the element
        const newPosition = elementLeftOffset + elementWidth / 2 - containerWidth / 2;
        // Clamp new scroll position to remain within the container bounds
        const clampedPosition = Math.max(
          0,
          Math.min(newPosition, container.scrollWidth - containerWidth),
        );

        container.scrollTo({
          left: clampedPosition,
          ...(smooth && { behavior: 'smooth' }),
        });
      }
    }, [activeIndex, alwaysScrollCenter, smooth]);

    return (
      <Flex
        ref={containerRef}
        className={cn(
          'relative scrollbar-hidden w-full touch-pan-x gap-2 overflow-x-auto overflow-y-hidden whitespace-nowrap',
          className,
        )}
        {...props}>
        {children}
      </Flex>
    );
  },
);

HorizontalAutoScroll.displayName = 'HorizontalAutoScroll';
